import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { pwaApi } from '../../lib/api.js';
import { useSessionStore } from '../../store/sessionStore.js';

interface ProductionRow {
  id: number;
  value: string;
  recordedAt: string;
}

export default function ProductionHistoryPage({ type }: { type: 'PRODUCED' | 'REJECT' | 'RATE' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sessionId, refreshProductionSummary } = useSessionStore();
  const [rows, setRows] = useState<ProductionRow[]>([]);
  const [edited, setEdited] = useState<Record<number, string>>({});
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [newValue, setNewValue] = useState('');
  const [loading, setLoading] = useState(true);

  const titles: Record<typeof type, string> = {
    PRODUCED: t('history.production.titleProduced'),
    REJECT: t('history.production.titleReject'),
    RATE: t('history.production.titleRate'),
  };
  const columnLabels: Record<typeof type, string> = {
    PRODUCED: t('history.production.columnProduced'),
    REJECT: t('history.production.columnReject'),
    RATE: t('history.production.columnRate'),
  };
  const title = titles[type];
  const columnLabel = columnLabels[type];

  async function load() {
    if (!sessionId) return;
    setLoading(true);
    const { data } = await pwaApi.get(`/tablet/sessions/${sessionId}/production`, { params: { type } });
    setRows(data.data as ProductionRow[]);
    setLoading(false);
  }

  useEffect(() => { load(); }, [sessionId, type]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleSelect(id: number) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function handleAddRow() {
    if (!sessionId || !newValue.trim()) return;
    await pwaApi.post(`/tablet/sessions/${sessionId}/production`, { type, value: Number(newValue) });
    setNewValue('');
    await load();
    await refreshProductionSummary();
  }

  async function handleSave() {
    if (!sessionId) return;
    const ids = Object.keys(edited).map(Number);
    await Promise.all(ids.map(id => pwaApi.put(`/tablet/sessions/${sessionId}/production/${id}`, { value: Number(edited[id]) })));
    setEdited({});
    await load();
    await refreshProductionSummary();
  }

  async function handleDeleteSelected() {
    if (!sessionId || selected.size === 0) return;
    await Promise.all(Array.from(selected).map(id => pwaApi.delete(`/tablet/sessions/${sessionId}/production/${id}`)));
    setSelected(new Set());
    await load();
    await refreshProductionSummary();
  }

  return (
    <div className="h-full overflow-y-auto" style={{ background: '#1a1a2e' }}>
      <div className="flex items-center gap-4 px-4 h-16 bg-gray-900 border-b border-gray-700">
        <button onClick={() => navigate('/workstation')} className="bg-gray-700 hover:bg-gray-600 active:bg-gray-500 text-white px-5 py-3 rounded-lg font-bold text-sm">
          ← {t('history.close')}
        </button>
        <div className="text-white font-bold">{title}</div>
      </div>

      <div className="p-4 flex items-center gap-3">
        <input
          type="number"
          className="bg-gray-800 text-white px-4 py-2 rounded border border-gray-600 focus:border-blue-500 focus:outline-none w-40"
          placeholder={t('process.value')}
          value={newValue}
          onChange={e => setNewValue(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleAddRow()}
        />
        <button onClick={handleAddRow} disabled={!newValue.trim()} className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-5 py-3 rounded-lg font-bold text-base">
          {t('history.new')}
        </button>
        <button onClick={handleSave} disabled={Object.keys(edited).length === 0} className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-5 py-3 rounded-lg font-bold text-base">
          {t('history.save')}
        </button>
        <button onClick={handleDeleteSelected} disabled={selected.size === 0} className="bg-red-700 hover:bg-red-600 disabled:bg-gray-600 text-white px-5 py-3 rounded-lg font-bold text-base">
          {t('history.deleteSelected')}
        </button>
      </div>

      <div className="px-4">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-800 text-gray-300 text-sm">
              <th className="p-2 w-14"></th>
              <th className="p-2 text-left">{columnLabel}</th>
              <th className="p-2 text-left">{t('history.time')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id} className="border-b border-gray-800">
                <td className="p-2 text-center">
                  <input type="checkbox" className="w-6 h-6 accent-blue-500" checked={selected.has(row.id)} onChange={() => toggleSelect(row.id)} />
                </td>
                <td className="p-2">
                  <input
                    type="number"
                    className="bg-white text-gray-900 px-3 py-2.5 rounded text-base w-32"
                    value={edited[row.id] ?? row.value}
                    onChange={e => setEdited(prev => ({ ...prev, [row.id]: e.target.value }))}
                  />
                </td>
                <td className="p-2 text-gray-400 text-sm">{new Date(row.recordedAt).toLocaleString('it-IT')}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={3} className="p-4 text-center text-gray-500 text-sm">{t('history.production.empty')}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
